import { FC, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Config, { priceForLanguage } from '../lib/Config'
import { Language } from '../lib/Enums/Language'
import { usePayment } from '../lib/Hooks/Payment'
import Headline from './Elements/Headline'
import { Modal } from './Elements/Modal'
import Text from './Elements/Text'
import Button from './Formik/Button'
import { SheetType } from './Sheets/lib/Enums/SheetType'
import { useCreateBudget } from './Sheets/lib/Hooks/Budget'

const HANDELSBETINGELSER_SLUG: Record<Language, string> = {
  [Language.DANISH]: 'handelsbetingelser',
  [Language.GERMAN]: 'geschäftsbedingungen',
  [Language.ENGLISH]: 'terms-and-conditions-2',
}

type Props = {
  show: boolean
  onHide: () => void
  sheetType: SheetType
  sheetId?: number
  name: string
  children?: any
  secondaryButtonLabel?: string
  // Shows the intro content on its own first, requiring a second click to
  // reach a separate "accept terms" confirmation step — used for onboarding
  // content (e.g. the welcome video). Everything else shows the checkbox
  // alongside the intro content in a single step.
  twoStep?: boolean
}
const Paywall: FC<Props> = ({
  show,
  onHide,
  sheetType,
  sheetId,
  name,
  children,
  secondaryButtonLabel,
  twoStep = false,
}) => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language as Language
  const acceptTermsId = useId()
  const [paymentComplete, togglePayment] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [showConfirmStep, setShowConfirmStep] = useState(false)

  const { mutateAsync: pay, isPending: isPaymentLoading } = usePayment()
  const { mutateAsync: createBudget } = useCreateBudget()

  // Only a real two-step flow when there's intro content to show before the
  // checkbox — with no children there's nothing to show first anyway.
  const isTwoStepIntro = twoStep && !!children && !showConfirmStep
  const atConfirmStep = !isTwoStepIntro
  const showChildrenContent = !!children && !(twoStep && showConfirmStep)

  const sheetPrice =
    sheetType === SheetType.BUDGET
      ? priceForLanguage(Config.PRICE_BUDGET, i18n.language)
      : sheetType === SheetType.SPREADSHEET
        ? priceForLanguage(
          Config.PRICE_SPREADSHEET,
          i18n.language,
          Config.PRICE_SPREADSHEET_EUR,
        )
        : priceForLanguage(Config.PRICE_ACCOUNTING, i18n.language)

  const sendPayment = async () => {
    const sheet = { sheetType, id: sheetId }

    if (!sheet.id) {
      const budget = await createBudget()
      sheet.id = budget.id
    }

    await pay(sheet as { sheetType: SheetType; id: number })

    togglePayment(true)
  }

  const handlePrimaryClick = () => {
    if (isTwoStepIntro) {
      setShowConfirmStep(true)
      return
    }

    sendPayment()
  }

  return (
    <Modal show={show} onHide={onHide}>
      {(closeModal: any) => (
        <>
          {!paymentComplete && (
            <>
              {showChildrenContent
                ? (
                  <>
                    {typeof children === 'function' && children(closeModal)}
                    {typeof children === 'object' && children}
                  </>
                )
                : (
                  <>
                    <Headline>
                      {t('common:paywall.purchaseTitle', { name })}
                    </Headline>
                    <Text className='mt-6'>
                      {t('common:paywall.purchaseDescriptionLine1', {
                        name,
                        price: sheetPrice,
                      })}
                    </Text>
                    <Text className='mt-3 mb-6'>
                      {t('common:paywall.purchaseDescriptionLine2', { name })}
                    </Text>
                  </>
                )}
              {atConfirmStep && (
                <div className='flex items-start gap-2 mb-4'>
                  <input
                    type='checkbox'
                    id={acceptTermsId}
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className='mt-1'
                  />
                  <label htmlFor={acceptTermsId}>
                    {t('common:paywall.acceptTermsPrefix')}{' '}
                    <a
                      className='underline'
                      href={`${Config.WWW_DOMAIN[locale] ?? Config.WWW_DOMAIN[Language.DANISH]}/${HANDELSBETINGELSER_SLUG[locale] ?? HANDELSBETINGELSER_SLUG[Language.DANISH]}`}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('common:paywall.termsLinkText')}
                    </a>.
                  </label>
                </div>
              )}
              <div className='flex justify-end gap-6'>
                {!atConfirmStep && secondaryButtonLabel && (
                  <Button theme='ghost' onClick={closeModal}>
                    {secondaryButtonLabel}
                  </Button>
                )}
                <Button
                  theme='blue'
                  onClick={handlePrimaryClick}
                  loading={isPaymentLoading}
                  disabled={atConfirmStep && !acceptedTerms}
                >
                  {t('common:paywall.pay', { price: sheetPrice })}
                </Button>
              </div>
            </>
          )}
          {paymentComplete && (
            <>
              <Headline>{t('common:paywall.thankYouHeading')}</Headline>
              <Text className='mt-3'>{t('common:paywall.thankYouBody1')}</Text>
              <Text className='mt-3'>
                {t('common:paywall.thankYouBody2', {
                  sheetType: t(
                    sheetType === SheetType.ACCOUNTING
                      ? 'common:paywall.accounting'
                      : sheetType === SheetType.SPREADSHEET
                        ? 'common:paywall.spreadsheet'
                        : 'common:paywall.budget',
                  ),
                })}
              </Text>
              <Text className='mt-3'>{t('common:paywall.thankYouBody3')}</Text>
              <Button className='mt-6' onClick={closeModal}>
                {t('common:paywall.closeWindow')}
              </Button>
            </>
          )}
        </>
      )}
    </Modal>
  )
}

export default Paywall
